/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useMemo, useContext, useCallback, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ValidationGroup, ErrorMessages } from '@bingads-webui-react/validation';
import Repository from '@bingads-webui-campaign-legacy/adextension-shared/service/repository';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup, FocusableActuator } from '@bingads-webui-campaign-react/actuator';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { DisclaimerAdExtension } from '@bingads-webui-campaign/campaign-ui-schemas';
import { DisclaimerStringMap } from '@bingads-webui-campaign-react/ads-campaign-disclaimers';
import {
  bidAdjustmentIcon,
  getAssociatedDisclaimer,
  getCampaignStyles,
  ValidatedDisclaimerExtension,
} from '@bingads-webui-campaign-react/display-plus-campaigns';
import {
  getConversionGoalTitleDetails,
  CampaignNamePanel,
  AdSchedulePanel,
  UrlOptionsPanel,
  PANEL_KEYS,
  LanguageTargetPanel,
  BidStrategyPanel,
  getFinalUrlExpansionTitleDetails,
  getStoreTitleDetails,
  BudgetPanel,
  getDisclaimerTitleDetails,
} from '@bingads-webui-campaign-react/wizard-shared-components';
import {
  Collapse,
  TextTitle,
  useCollapseState,
  collapseExpander,
  ValidatedPanel,
  getPanelErrorProps,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { useFormService } from '@bingads-webui-react/form';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ValidatedConversionGoals, ValidatedConversionGoalsScopeSelector, useIsMounted, LocationTargetingSettingPanel, BrandShowroomPanel } from '@bingads-webui-campaign-react/step-wizard-controls';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import {
  FormErrorGroup,
  UnmappedFormErrors as OriginalUnmappedFormErrors,
  entityIs,
  andComp,
  anyComp,
  notComp,
  propertyIs,
  StandardControlAdapter
} from '@bingads-webui-campaign-react/form-error';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import {
  apiErrorMapper,
  ERROR_ENTITY,
  getStepCampaignSettingsStyles,
  ConversionsImg,
  FinalUrlExpansionsImg,
  PageFeedsImg,
  AutoGenAssetsImg,
  BrandExclusionsImg,
  ShoppingImg,
} from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { Campaign } from '@bingads-webui/campaign-odata-schemas';
import { withControlErrorHandling, withUnvalidatedControlErrors, withContainerErrors, FormErrorBeaconAdapter } from '@bingads-webui-campaign-react/error-handling';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { Link, Stack, Label } from '@bingads-webui-react/fabric-control';
import { ValidatedLegacyFinalUrlExpansion } from '@bingads-webui-campaign-react/legacy-final-url-expansion';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { PageFeedSelectorSection } from '@bingads-webui-campaign-react/page-feed-selector';
import { BrandListsPicker } from '@bingads-webui-campaign-react/brand-lists-components';
import { ValidatedAutoGeneratedAssets } from '@bingads-webui-campaign-react/auto-generated-assets';
import { StoreSelectContainer } from '@bingads-webui-campaign-react/store-select';
import { MAX_CONVERSIONS, MAX_CONVERSIONS_VALUE } from '@bingads-webui-campaign-react/bid-strategy-component';
import { getMessageType } from '@bingads-webui-campaign-react/campaign-settings-utils';
import { useWizardService, WIZARD_EVENTS, getWizardData } from '@bingads-webui-react/wizard';
import { ValidatedNewCustomerAcquisition, ValidatedNewCustomerAcquisitionHeader, newCustomerAcquisitionServerErrorFilter } from '@bingads-webui-campaign-react/new-customer-acquisition';
import { PerformanceMarkerContext } from '@bingads-webui-campaign-react/performance-marker-provider';

import { RadioShimmer, StoreSelectorShimmer, SingleLineShimmer } from '../../shared/components/section-shimmer';
import { PMAX_WIZARD_ID_NAME } from '../../constants';
import { getCampaignSettingsPanels } from '../step-constants';

const getCampaignStylesNorthStar = () => ({
  header: {
    marginBottom: 20,
  },
  formContainer: {
    maxWidth: 800,
    '& .advanced-scheduling-grid': {
      width: 'unset !important',

      '& .ad-scheduling-grid': {
        minWidth: 'unset !important',
      },
    },
    '& .title-item': {
      width: 'unset !important',
    },
    '& .goal-selector-with-scope': {
      width: '100%',
      '& .ba-goal-selector': {
        width: '100%',
        ' & .multi-selector': {
          width: '100%',
          '& .multi-selector-content': {
            width: '100%',
            '& .ba-entity-selector': {
              minWidth: '50% !important',
              '& .list-selector-container': {
                width: 'unset !important',
                '& .ba-list-selector': {
                  width: 'unset !important',
                },
              },
            },
          },
        },
      },
    },
    '& .bid-strategy-dropdown': {
      '& .ms-Dropdown': {
        maxWidth: 312,
      },
    },
  },
});

const entityIsCampaign = entityIs(ERROR_ENTITY.Campaign);

const campaignUrlOptionsProperties = andComp(
  entityIsCampaign,
  anyComp(
    propertyIs('TrackingUrlTemplate'),
    propertyIs('FinalUrlSuffix'),
    propertyIs('UrlCustomParameters')
  )
);

const notCampaignUrlOptionsProperties = notComp(campaignUrlOptionsProperties);

const UnmappedFormErrors = withControlErrorHandling(OriginalUnmappedFormErrors);

const feedAssociationsProp = andComp(entityIsCampaign, propertyIs('FeedAssociations'));
const campaignNegativeWebPages = entityIs(ERROR_ENTITY.CampaignNegativeWebpage);
const brandListAssociationEntity = entityIs(ERROR_ENTITY.BrandListAssociation);

const ServerValidatedLegacyFinalUrlExpansion = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLegacyFinalUrlExpansion, { filter: campaignNegativeWebPages }));
const ServerValidatedPageFeedSelectorSection = withControlErrorHandling(withContainerErrors(PageFeedSelectorSection, { filter: feedAssociationsProp, Adapter: StandardControlAdapter() }), [FocusableActuator], [FormErrorBeaconAdapter]);
const ServerValidatedBrandListsPicker = withControlErrorHandling(withContainerErrors(BrandListsPicker, { filter: brandListAssociationEntity, Adapter: StandardControlAdapter() }), [FocusableActuator], [FormErrorBeaconAdapter]);
const ServerValidatedAutoGeneratedAssets = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedAutoGeneratedAssets, { filter: campaignNegativeWebPages }));
const ServerValidatedNewCustomerAcquisition = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedNewCustomerAcquisition, { filter: newCustomerAcquisitionServerErrorFilter }));

export const CampaignSettingStepShell = ({
  data: campaignInfo,
  onDataChange,
  error,
}) => {
  const isLoading = useMemo(() => _.isEmpty(campaignInfo), [campaignInfo]);

  const {
    campaign,
    brandExclusions,
    budgetAmount,
    currency,
    bidStrategy,
  } = campaignInfo || {};
  const {
    Id: campaignId,
    conversionGoals: conversionGoalsState,
    finalUrlExpansionOptOut,
    autoGeneratedTextOptOut,
    autoGeneratedImageOptOut,
    finalUrlExpansions,
    IsBSREnabled,
  } = campaign || {};
  const {
    savedAddedBrandListIds,
    savedDeletedBrandListIds,
  } = brandExclusions || {};

  const { serverErrors, systemErrors } = error || {};

  const classes = useDefaultStyles(getStepCampaignSettingsStyles);
  const styles = useDefaultStyles(getCampaignStylesNorthStar);
  const campaignClasses = useDefaultStyles(getCampaignStyles);

  const { state } = useFormService();
  const { config: wizardConfig, state: wizardState, send } = useWizardService();
  const wizardData = getWizardData(wizardState);

  const { saveFailed } = state.context;
  const i18n = useContext(I18nContext);
  const isMounted = useIsMounted();
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap, DisclaimerStringMap]), [i18n]);
  const { scope } = useContext(ScopeContext) || {};
  const serverContext = useContext(ServerContext);
  const {
    Permissions, DynamicPermissions, CurrentAccount,
  } = serverContext || {};
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);
  const { AdvertiserCustomerID: advertiserCustomerId } = CurrentAccount || {};
  const {
    EnableBudgetSuggestion: showBudgetSuggestion,
    ConversionAssociationWarningP2: IsConversionAssociationWarningP2Enabled,
    ConversionWarningImprovement: ConversionWarningImprovementEnabled,
    PMaxDisclaimers,
  } = permissions.dynamic;

  const {
    IsDomesticSimplifiedChineseEnabled,
    IsMSANDisclaimerEnabled,
  } = permissions;
  const isPMaxDisclaimersEnabled = !IsDomesticSimplifiedChineseEnabled && PMaxDisclaimers && IsMSANDisclaimerEnabled;

  const perfMarker = useContext(PerformanceMarkerContext);
  const [disclaimerLoadPerfMarker] = useState(() => perfMarker.createChild('Pmax_Disclaimers_Campaign_Settings_Load_Perf_Marker'));
  const [disclaimerSavePerfMarker] = useState(() => perfMarker.createChild('Pmax_Disclaimers_Campaign_Settings_Save_Perf_Marker'));

  const conversionGoals = useMemo(() => ({ conversionGoals: conversionGoalsState }), [conversionGoalsState]);
  const conversionGoalsTitleDetails = useMemo(() => getConversionGoalTitleDetails(i18n, conversionGoals && conversionGoals.conversionGoals), [i18n, conversionGoals]);
  const [reportedSelectedPageFeeds, setReportedSelectedPageFeeds] = useState([]);
  const onSelectedPageFeedsUpdate = useCallback((selectedPageFeeds) => {
    if (!isMounted.current) return;
    setReportedSelectedPageFeeds(selectedPageFeeds);
  }, [isMounted]);
  const pageFeedsDetails = useMemo(
    () => {
      if (_.isEmpty(reportedSelectedPageFeeds)) {
        return i18n.getString(_TL_('Upload all the relevant URLs for your campaign by spreadsheet.'));
      }
      if (reportedSelectedPageFeeds.length === 1) {
        return i18n.getString(_TL_('You’ve successfully added 1 page feed.'));
      }
      return i18n.getString(_TL_('You’ve successfully added {{number}} page feeds.'), { number: reportedSelectedPageFeeds.length });
    },
    [i18n, reportedSelectedPageFeeds]
  );
  const onPageFeedsChange = useCallback((selectedPageFeeds) => {
    if (!isMounted.current) return;
    onDataChange({
      data: prevData => ({
        ...prevData,
        campaign: {
          ...prevData.campaign,
          pageFeeds: selectedPageFeeds,
        },
      }),
      isDirty: true,
    });
  }, [isMounted, onDataChange]);
  const autoGenAssetsSummary = useMemo(() => {
    if (finalUrlExpansionOptOut && autoGeneratedTextOptOut && autoGeneratedImageOptOut) {
      return i18n.getString(_TL_('Off: Do not use autogenerated assets in ads.'));
    }
    return i18n.getString(_TL_('On: You’ve enabled autogenerated assets for your ads.'));
  }, [i18n, finalUrlExpansionOptOut, autoGeneratedTextOptOut, autoGeneratedImageOptOut]);

  const autoGenAssetsData = useMemo(() => ({
    finalUrlExpansions,
    options: {
      finalUrlExpansionOptOut,
      autoGeneratedTextOptOut,
      autoGeneratedImageOptOut,
    },
  }), [autoGeneratedImageOptOut, autoGeneratedTextOptOut, finalUrlExpansionOptOut, finalUrlExpansions]);

  const onBrandListsChange = useCallback((selectedBrandLists, [deletedBrandListIds, addedBrandListIds]) => {
    if (!isMounted.current) return;
    onDataChange({
      data: prevData => ({
        ...prevData,
        brandExclusions: {
          ...prevData.brandExclusions,
          deletedBrandListIds,
          addedBrandListIds,
        },
      }),
      isDirty: true,
    });
  }, [isMounted, onDataChange]);
  const [reportedSelectedBrandLists, setReportedSelectedBrandLists] = useState([]);
  const onSelectedBrandListsUpdate = useCallback((selectedBrandLists) => {
    if (!isMounted.current) return;
    setReportedSelectedBrandLists(selectedBrandLists);
  }, [isMounted]);
  const brandListsDetails = useMemo(
    () => {
      if (_.isEmpty(reportedSelectedBrandLists)) {
        return i18n.getString(_TL_('Add brand exclusions so that your ads won’t show on search queries that mention those brands.'));
      }
      if (reportedSelectedBrandLists.length === 1) {
        return i18n.getString(_TL_('You’ve added 1 brand list, which makes it so that your ads won’t show on search queries that mention those brands.'));
      }
      return i18n.getString(_TL_('You’ve added {{number}} brand lists, which makes it so that your ads won’t show on search queries that mention those brands.'), { number: reportedSelectedBrandLists.length });
    },
    [i18n, reportedSelectedBrandLists]
  );
  const [finalUrlExpansionsView, setFinalUrlExpansionsView] = useState(null);
  const setFinalUrlExpansionsViewCallback = useCallback((view) => {
    setFinalUrlExpansionsView(view);
  }, []);

  const DisclaimerAdsEnabled = useMemo(
    () =>
      _.result(_.find( // from campaign setting DB
        _.get(campaign, 'CampaignSettings', []),
        setting => setting['@odata.type'] === '#Model.DisclaimerSetting'
      ), 'DisclaimerAdsEnabled', false)
    , [campaign]
  );
  const disclaimerRepositoryRef = useRef(new Repository({
    schema: DisclaimerAdExtension,
    skipExtensionCache: true,
  }));

  const [associatedDisclaimer, setAssociatedDisclaimer] = useState(null);
  useEffect(() => {
    if (isPMaxDisclaimersEnabled && campaignId) {
      getAssociatedDisclaimer(campaignId, disclaimerRepositoryRef.current, disclaimerLoadPerfMarker).then((disclaimer) => {
        if (!_.isEmpty(disclaimer)) {
          setAssociatedDisclaimer(disclaimer);
        }
      });
    }
    return null;
  }, [isPMaxDisclaimersEnabled, campaignId, disclaimerLoadPerfMarker]);

  const disclaimersDetails = useMemo(() => getDisclaimerTitleDetails(i18n, { selectedDisclaimer: associatedDisclaimer }), [associatedDisclaimer, i18n]);
  const isCampaignShoppingType = useMemo(
    // check if the odata type is ShoppingSettings, return true if it is, false if not
    () => !!_.find(_.get(campaign, 'CampaignSettings', []), setting => setting['@odata.type'] === '#Model.ShoppingSettings')
    , [campaign]
  );

  const { basicPanels, advancedPanels } = useMemo(() => getCampaignSettingsPanels(permissions, isCampaignShoppingType), [permissions, isCampaignShoppingType]);

  const [
    allCampaignSettingsSections,
    campaignSettingsExpandedSections,
    setCampaignSettingsExpandedSections,
  ] = useCollapseState(
    ['campaign'] /* all sections */,
    ['campaign'] /* initially expanded sections */
  );

  const basicSettingsSection = useMemo(() => {
    const campaignNamePanel = (
      <CampaignNamePanel
        key={PANEL_KEYS.CAMPAIGN_NAME}
        isLoading={isLoading}
      />
    );

    const generalSettings = [
      campaignNamePanel,
    ];

    return (
      <div className={classNames('basic-campaign-section')}>
        <Collapse allPanelItems={allCampaignSettingsSections} checkItems skipItemsTypeCheck activeKeys={campaignSettingsExpandedSections} setActiveKeys={setCampaignSettingsExpandedSections}>
          { generalSettings }
        </Collapse>
      </div>
    );
  }, [isLoading, allCampaignSettingsSections, campaignSettingsExpandedSections, setCampaignSettingsExpandedSections]);

  const [
    allMoreSettingsSections,
    moreSettingsExpandedSections,
    setMoreSettingsExpandedSections,
    flipMoreSettingsExpandAllState,
  ] = useCollapseState(
    advancedPanels /* all sections */,
    [PANEL_KEYS.BRAND_SHOWROOM] /* initially expanded sections */
  );
  const moreBasicSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setMoreSettingsExpandedSections }),
    [setMoreSettingsExpandedSections]
  );

  const moreBasicSettingsSection = useMemo(() => (
    <div className={classNames('more-basic-campaign-section', classes.moreBasicCampaignSection)}>
      <Stack horizontal horizontalAlign="space-between" tokens={{ padding: '0 0 12px 0' }}>
        <Label>{i18n.getString(_TL_('Advanced campaign settings'))}</Label>
        <Link className="jumbo-card-control-all-btn" onClick={flipMoreSettingsExpandAllState}>{moreSettingsExpandedSections.length ? i18n.getString(_TL_('Collapse all')) : i18n.getString(_TL_('Expand all'))}</Link>
      </Stack>
      <Collapse allPanelItems={allMoreSettingsSections} checkItems skipItemsTypeCheck activeKeys={moreSettingsExpandedSections} setActiveKeys={setMoreSettingsExpandedSections}>
        <AdSchedulePanel
          key={PANEL_KEYS.AD_SCHEDULE}
          isLoading={isLoading}
          expand={moreBasicSettingsExpand}
          data={campaign}
        />
        <ValidatedPanel
          key={PANEL_KEYS.FINAL_URL_EXPANSION}
          title={<TextTitle
            titleKey={_TL_('Final URL expansion')}
            topicKey="pop_BA_PMAX_FinalURLExpansion"
            details={getFinalUrlExpansionTitleDetails(i18n, finalUrlExpansionsView && finalUrlExpansionsView.current, !finalUrlExpansionOptOut)}
            icon={FinalUrlExpansionsImg}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
            expand: moreBasicSettingsExpand,
            expandKey: PANEL_KEYS.FINAL_URL_EXPANSION,
        }}
        >
          <ServerValidatedLegacyFinalUrlExpansion
            campaign={{ campaign }}
            validationProps={{
            data: finalUrlExpansions,
          }}
            setFinalUrlExpansionsView={setFinalUrlExpansionsViewCallback}
            isFinalUrlExpansionEnabled={!finalUrlExpansionOptOut}
          />
        </ValidatedPanel>
        <BrandShowroomPanel
          key={PANEL_KEYS.BRAND_SHOWROOM}
          switchValidationProps={{
            field: 'IsBSREnabled',
          }}
          radioValidationProps={{
            field: 'BSRAdDistribution',
          }}
          isEnabled={IsBSREnabled}
        />
        <ValidatedPanel
          key={PANEL_KEYS.AUTO_GEN_ASSETS}
          title={<TextTitle
            titleKey={_TL_('Autogenerated assets')}
            topicKey="pop_BA_PMAX_AutoAssets"
            details={autoGenAssetsSummary}
            icon={AutoGenAssetsImg}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
              expand: moreBasicSettingsExpand,
              expandKey: PANEL_KEYS.AUTO_GEN_ASSETS,
            }}
        >
          <FluentProvider theme={webLightTheme}>
            <ServerValidatedAutoGeneratedAssets
              validationProps={{
                  data: autoGenAssetsData,
                }}
            />
          </FluentProvider>
        </ValidatedPanel>
        <ValidatedPanel
          key={PANEL_KEYS.PAGE_FEEDS}
          title={<TextTitle
            titleKey={_TL_('Page feeds')}
            topicKey="pop_BA_PMAX_PageFeeds"
            details={pageFeedsDetails}
            icon={PageFeedsImg}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
            expand: moreBasicSettingsExpand,
            expandKey: 'page-feeds',
          }}
        >
          <FluentProvider theme={webLightTheme}>
            { isLoading ? <SingleLineShimmer /> :
            <ServerValidatedPageFeedSelectorSection cid={advertiserCustomerId} aid={scope.values.aid} campaignId={campaignId} onChange={onPageFeedsChange} onSelectedOptionsUpdate={onSelectedPageFeedsUpdate} />
              }
          </FluentProvider>
        </ValidatedPanel>
        <UrlOptionsPanel
          key={PANEL_KEYS.URL_OPTIONS}
          isLoading={isLoading}
          expand={moreBasicSettingsExpand}
          data={campaign}
        />
        <ValidatedPanel
          key={PANEL_KEYS.BRAND_EXCLUSIONS}
          title={<TextTitle
            titleKey={_TL_('Brand exclusions')}
            topicKey="pop_BA_PMAX_BrandExclusions"
            details={brandListsDetails}
            icon={BrandExclusionsImg}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
              expand: moreBasicSettingsExpand,
              expandKey: PANEL_KEYS.BRAND_EXCLUSIONS,
            }}
        >
          <FluentProvider theme={webLightTheme}>
            {
              isLoading ? <SingleLineShimmer /> : (
                <ServerValidatedBrandListsPicker
                  className="pmax-campaign-brand-lists-picker"
                  cid={advertiserCustomerId}
                  aid={scope.values.aid}
                  campaignId={campaignId}
                  isConfirmDialogOn
                  onChange={onBrandListsChange}
                  onSelectedOptionsUpdate={onSelectedBrandListsUpdate}
                  savedAddedSelectedOptionsKeys={savedAddedBrandListIds}
                  savedDeletedSelectedOptionsKeys={savedDeletedBrandListIds}
                  descriptionTextTemplate={i18n.getString(_TL_('Add brand exclusions so that your ads won’t show on search queries that mention those brands. You can add up to {{count}} brand lists to your Performance Max campaign. To manage your brand lists, go to {{link}}Brand lists{{/link}}. {{aboutLink}}Learn more about brand exclusions and troubleshoot errors in case your brand is not excluded.{{/aboutLink}}'))}
                />
              )
            }

          </FluentProvider>
        </ValidatedPanel>
        <ValidatedPanel
          actuationProps={{
              expand: moreBasicSettingsExpand,
              expandKey: PANEL_KEYS.DISCLAIMERS,
            }}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          key={PANEL_KEYS.DISCLAIMERS}
          title={
            <TextTitle
              details={disclaimersDetails}
              icon={bidAdjustmentIcon}
              titleKey={_TL_('Disclaimers')}
            />
            }
        >
          {isLoading ? <SingleLineShimmer /> : (
            <ValidatedDisclaimerExtension
              className={classNames(campaignClasses.disclaimerContainerWrapper, 'disclaimer__container')}
              campaignId={campaignId}
              i18n={legacyI18n}
              repository={disclaimerRepositoryRef.current}
              schema={DisclaimerAdExtension}
              validationProps={{
                  data: {
                    DisclaimerAdsEnabled,
                    AdExtensionAssociations: [],
                    associatedDisclaimer,
                  },
                }}
              AllowDisclaimerEnabled
              showOnlyPopupLayout
              usePanel
              perfMarker={disclaimerSavePerfMarker}
            />
          )}
        </ValidatedPanel>

      </Collapse>
    </div>
  ), [classes.moreBasicCampaignSection, i18n, flipMoreSettingsExpandAllState, moreSettingsExpandedSections, allMoreSettingsSections, setMoreSettingsExpandedSections, isLoading, moreBasicSettingsExpand, campaign, finalUrlExpansionsView, finalUrlExpansionOptOut, finalUrlExpansions, setFinalUrlExpansionsViewCallback, autoGenAssetsSummary, autoGenAssetsData, pageFeedsDetails, advertiserCustomerId, scope.values.aid, campaignId, onPageFeedsChange, onSelectedPageFeedsUpdate, brandListsDetails, onBrandListsChange, onSelectedBrandListsUpdate, savedAddedBrandListIds, savedDeletedBrandListIds, disclaimersDetails, campaignClasses.disclaimerContainerWrapper, legacyI18n, DisclaimerAdsEnabled, associatedDisclaimer, disclaimerSavePerfMarker, IsBSREnabled]);

  const onCampaignValidation = useCallback((results) => {
    if (!isMounted.current) return;

    onDataChange({
      data: (prevData) => {
        const patchedData = {
          ...prevData,
          campaign: {
            ...prevData.campaign,
            ...results.data,
          },
        };
        if (IsConversionAssociationWarningP2Enabled && wizardConfig && wizardConfig.id === PMAX_WIZARD_ID_NAME.PMAX_NORTH_STAR_SETTING_WIZARD) {
          const message = getMessageType(prevData, patchedData);

          if (message) {
            const updatedData = {
              wizardData: {
                ...wizardData,
                customConfirmMessages: [message],
              },
            };

            send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, updatedData.wizardData);
          }
        }
        return ({
          ...patchedData,
        });
      },
      isValid: results.isValid,
      isDirty: results.isDirty,
    });
  }, [IsConversionAssociationWarningP2Enabled, isMounted, onDataChange, send, wizardConfig, wizardData]);

  const [
    allSections,
    allExpandedSections,
    setExpandedSections,
  ] = useCollapseState(
    basicPanels,
    [PANEL_KEYS.BID_STRATEGY, PANEL_KEYS.BUDGET, PANEL_KEYS.LOCATIONS, PANEL_KEYS.LANGUAGE_TARGET, PANEL_KEYS.AD_SCHEDULE, PANEL_KEYS.URL_OPTIONS, PANEL_KEYS.CONVERSIONS, PANEL_KEYS.NEW_CUSTOMER_ACQUISITION]
  );

  const expandSections = useMemo(() =>
    collapseExpander({ setActiveKeys: setExpandedSections }), [setExpandedSections]);

  const {
    bundleCountries,
    bundleLocationCriterions,
  } = campaignInfo || {};

  const {
    ProviderId,
    IsFeedFiltered,
    SalesCountry,
    FeedLabel,
    LocalInventoryAdsEnabled,
    newCustomerAcquisition,
    newCustomerAcquisitionOptOut,
  } = campaign || {};

  const storeContainerData = useMemo(() => ({
    ProviderId,
    IsFeedFiltered,
    SalesCountry,
    FeedLabel,
    LocalInventoryAdsEnabled,
  }), [ProviderId, IsFeedFiltered, SalesCountry, FeedLabel, LocalInventoryAdsEnabled]);
  const [storesData, setStoresData] = useState({ error: undefined, stores: {}, countries: {} });
  const storeTitleDetails = useMemo(() => storesData && getStoreTitleDetails(i18n, storesData, storeContainerData), [i18n, storesData, storeContainerData]);
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const onStoreDataLoaded = useCallback(({ error, stores = {}, countries = {} }) => {
    if (isMounted.current) {
      setStoresData({ error, stores, countries });
    }
  }, [isMounted]);

  return (
    <ActuationGroup actuationProps={{ isActive: saveFailed }}>
      <AttentionBeaconGroup>
        <FormErrorGroup formErrorProps={{ errors: serverErrors, saveFailed, errorMapper: apiErrorMapper }}>
          <div className="campaign-step-container">
            <UnmappedFormErrors className="other-server-errors" filter={notCampaignUrlOptionsProperties} />
            <div className="system-errors">
              <ErrorMessages messages={systemErrors} />
            </div>
            <div className={styles.formContainer}>
              <ValidationGroup
                validationProps={{
                  schema: Campaign,
                  onValidate: onCampaignValidation,
                  data: campaign,
                  i18n: legacyI18n,
                }}
              >
                { basicSettingsSection }
                <Collapse
                  allPanelItems={allSections}
                  skipItemsTypeCheck
                  activeKeys={allExpandedSections}
                  setActiveKeys={setExpandedSections}
                  checkItems
                >
                  <BudgetPanel
                    key={PANEL_KEYS.BUDGET}
                    isLoading={isLoading}
                    expand={expandSections}
                    onDataChange={onDataChange}
                    data={{
                      budgetAmount,
                      currency,
                      campaignId,
                      campaignType: 'performanceMax',
                      showBudgetSuggestion,
                      isCampaignSetting: true,
                      campaign,
                    }}
                  />
                  <BidStrategyPanel
                    key={PANEL_KEYS.BID_STRATEGY}
                    isLoading={isLoading}
                    expand={expandSections}
                    onDataChange={onDataChange}
                    data={campaignInfo}
                    options={{
                    bidStrategyComponentOptions: {
                      bidStrategyOptions: [MAX_CONVERSIONS, MAX_CONVERSIONS_VALUE],
                    },
                    biddingBannerOptions: {
                      isCampaignCreation: false,
                    },
                    scenario: 'PmaxNorthStarSettingWizard',
                  }}
                  />
                  <ValidatedPanel
                    key={PANEL_KEYS.NEW_CUSTOMER_ACQUISITION}
                    title={
                      <ValidatedNewCustomerAcquisitionHeader
                        validationProps={{ data: { newCustomerAcquisitionOptOut } }}
                      />
                      }
                    attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                    actuationProps={{
                        expand: PANEL_KEYS.NEW_CUSTOMER_ACQUISITION,
                        expandKey: expandSections,
                      }}
                  >
                    {
                        isLoading ? <SingleLineShimmer /> : (
                          <ServerValidatedNewCustomerAcquisition
                            validationProps={{ data: { newCustomerAcquisition } }}
                            cid={advertiserCustomerId}
                            aid={scope.values.aid}
                            bidStrategy={bidStrategy}
                            optOut={newCustomerAcquisitionOptOut}
                          />
                        )
                      }
                  </ValidatedPanel>

                  <LocationTargetingSettingPanel
                    key={PANEL_KEYS.LOCATIONS}
                    isLoading={isLoading}
                    options={{
                    bundleCountries,
                    bundleLocationCriterions,
                  }}
                    expand={expandSections}
                    data={campaignInfo && campaignInfo.campaign}
                  />
                  <LanguageTargetPanel
                    key={PANEL_KEYS.LANGUAGE_TARGET}
                    isLoading={isLoading}
                    expand={expandSections}
                    data={campaign}
                  />
                  <ValidatedPanel
                    key={PANEL_KEYS.CONVERSIONS}
                    title={<TextTitle
                      titleKey={_TL_('Conversions')}
                      topicKey="pop_BA_PMAX_Conversions"
                      details={conversionGoalsTitleDetails}
                      icon={ConversionsImg}
                    />}
                    attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                    actuationProps={{
                      expand: { expandSections },
                      expandKey: 'conversions',
                    }}
                  >
                    {!ConversionWarningImprovementEnabled && (isLoading ? <RadioShimmer /> : (
                      <ValidatedConversionGoals
                        campaign={{ data: { ...campaign } }}
                        validationProps={{
                          initialValue: conversionGoals,
                          data: conversionGoals,
                        }}
                        cid={advertiserCustomerId}
                        aid={scope.values.aid}
                        showTrackingStatusIcon={IsConversionAssociationWarningP2Enabled}
                      />
                    ))}
                    {ConversionWarningImprovementEnabled && (isLoading ? <RadioShimmer /> : (
                      <ValidatedConversionGoalsScopeSelector
                        campaign={{ data: { ...campaign } }}
                        validationProps={{
                          initialValue: conversionGoals,
                          data: conversionGoals,
                        }}
                        cid={advertiserCustomerId}
                        aid={scope.values.aid}
                      />
                    ))}
                  </ValidatedPanel>

                  <ValidatedPanel
                    key={PANEL_KEYS.SHOPPING}
                    title={<TextTitle
                      titleKey={_TL_('Shopping settings')}
                      topicKey="pop_BA_PMAX_Conversions"
                      details={storeTitleDetails}
                      icon={ShoppingImg}
                    />}
                    attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                    actuationProps={{
                      expand: expandSections,
                      expandKey: PANEL_KEYS.SHOPPING,
                    }}
                  >
                    {isLoading ? <StoreSelectorShimmer /> : (
                      <StoreSelectContainer
                        campaign={{ data: { ...campaign } }}
                        data={storeContainerData}
                        onStoreDataLoaded={onStoreDataLoaded}
                        onRenderCustomLoadingComponent={() => <StoreSelectorShimmer />}
                      />
                    )}
                  </ValidatedPanel>
                </Collapse>
                { moreBasicSettingsSection }
              </ValidationGroup>
            </div>
          </div>
        </FormErrorGroup>
      </AttentionBeaconGroup>
    </ActuationGroup>
  );
};

CampaignSettingStepShell.propTypes = {
  data: PropTypes.shape({
    campaign: PropTypes.shape({}),
    adSchedules: PropTypes.shape({}),
    conversionGoalsState: PropTypes.shape({}),
  }).isRequired,
  error: PropTypes.shape({
    serverErrors: PropTypes.arrayOf(PropTypes.shape({})),
    systemErrors: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
};
